import {
    Component,
    effect,
    forwardRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    viewChild
} from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Account } from '@trade-platform/ui-shared';
import { debounceTime } from 'rxjs/operators';
import {
    AixDataTestingDirective,
    AixDropdownComponent,
    AixNextPendingFieldDirective
} from '@trade-platform/ui-components';
import { AUTOSELECT_SETTLE_MS } from './account-registration.constants';

@Component({
    selector: 'aix-account-registration',
    templateUrl: './account-registration.html',
    styleUrls: ['./account-registration.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AixAccountRegistrationGroupComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AixAccountRegistrationGroupComponent
        }
    ],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AixDropdownComponent,
        AixDataTestingDirective,
        AixNextPendingFieldDirective
    ]
})
export class AixAccountRegistrationGroupComponent
    implements ControlValueAccessor, OnInit, OnDestroy
{
    private _fb = inject(UntypedFormBuilder);

    accountDropdownRef = viewChild<AixDropdownComponent>(AixDropdownComponent);

    isLoadingAccounts = input<boolean>();
    accounts = input<Account[]>();
    filtering = input<boolean>(false);

    addAccountDetails = output();
    selectAccount = output<any>();
    filterAccount = output<string>();

    form: UntypedFormGroup;

    isDestroying = false;
    isActionButtonSelected = false;
    subscriptions: Subscription[] = [];

    private debouncer: Subject<string> = new Subject<string>();
    private _onChange: (value: any | null | undefined) => void;
    private autoselectTimer: ReturnType<typeof setTimeout> | null = null;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this._createFormGroup();
        this._setupObservables();

        effect(() => {
            const filtering = this.filtering();
            const loading = this.isLoadingAccounts();
            const accounts = this.accounts();

            // Every state change cancels any pending fire. This is the
            // safety net for all cancellation paths (keystroke, context
            // change, response settle, component destroy via ngOnDestroy).
            this.cancelAutoselectTimer();

            if (loading) return;
            if (accounts?.length) return;

            if (!filtering) {
                // Fresh context (initial mount, post-resetForContextChange).
                // Fire immediately — preserves PR #5194's "auto-prompt when
                // rep code has no accounts" intent.
                this.accountDropdownRef()?.onClickActionButton();
            } else {
                // Typed search settled with empty results. Defer the
                // autoselect by AUTOSELECT_SETTLE_MS to confirm the user
                // is done typing. Any new state change above this effect
                // re-runs and cancels via cancelAutoselectTimer().
                this.autoselectTimer = setTimeout(() => {
                    this.autoselectTimer = null;
                    // Re-check state at fire time. clearTimeout is generally
                    // synchronous, but at the exact boundary of "callback
                    // about to fire" vs "cancel called," there is no formal
                    // guarantee. Belt-and-suspenders.
                    if (!this.isLoadingAccounts() && !this.accounts()?.length && this.filtering()) {
                        this.accountDropdownRef()?.onClickActionButton();
                    }
                }, AUTOSELECT_SETTLE_MS);
            }
        });
    }

    ngOnInit() {
        this.isDestroying = false;
        this.patchForm();
    }

    onFilterAccounts(event: string) {
        // Cancel any pending autoselect timer synchronously on the
        // keystroke itself. The effect at line 90 only re-runs when
        // one of its tracked signals (filtering, isLoadingAccounts,
        // accounts) changes — and none of those flip until the 300ms
        // debounce expires and the parent dispatches a new fetch. That
        // leaves a window where the 2s autoselect timer could fire
        // mid-typing, reintroducing the #14127 race. Cancelling here
        // makes keystrokes a real cancellation source.
        this.cancelAutoselectTimer();
        this.debouncer.next(event);
    }

    onClickDropdownAction() {
        this.isActionButtonSelected = this.accountDropdownRef()?.isActionButtonSelected || false;
        this.addAccountDetails.emit();
        this.form.get('accountSelected')?.clearValidators();
        this.form.get('accountSelected')?.updateValueAndValidity();
        this.form.updateValueAndValidity();
    }

    patchForm() {
        const data = {};

        this.form.patchValue(data);
    }

    validate(ctrl: UntypedFormControl) {
        if (this.accountDropdownRef()?.isActionButtonSelected) {
            return null;
        }

        return this.form.get('accountSelected')?.value?.length || this.isDestroying
            ? null
            : { invalid: true };
    }

    writeValue(value: any | null | undefined): void {
        // CVA Parent → Child contract: when the parent's form control value
        // changes (e.g., via form.reset() or form.setValue()), update the
        // internal form to match. `emitEvent: false` prevents the resulting
        // valueChanges from cascading back through _onChange and re-triggering
        // writeValue (infinite loop). Resetting isActionButtonSelected on any
        // parent-driven write matches user intent — if the parent overrides
        // the value, the action-button state is no longer applicable.
        //
        // Call resetSearchInput() on the inner dropdown so its OWN
        // isActionButtonSelected flag is cleared too. The wrapper's
        // validate() reads the dropdown child's flag, not the wrapper's,
        // so clearing only this.isActionButtonSelected here is not enough.
        if (!value) {
            this.form.patchValue({ accountSelected: '' }, { emitEvent: false });
            this.isActionButtonSelected = false;
            this.accountDropdownRef()?.resetSearchInput();
            return;
        }
        // Reset the dropdown's display state BEFORE propagating the value.
        // patchValue here synchronously fans out through the formControlName
        // chain to the inner dropdown's CVA writeValue → _applySelection,
        // which sets searchFieldValueLabel to the matching option's label
        // and calls detectChanges. Calling resetSearchInput() AFTER the
        // patch would wipe that just-set label and leave a populated
        // control with a blank visible input. Order is load-bearing.
        this.accountDropdownRef()?.resetSearchInput();
        this.form.patchValue(value, { emitEvent: false });
    }

    registerOnChange(fn: (value: any | null | undefined) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // TODO: implement this method
        // throw new Error('registerOnTouched not implemented');
    }

    private _createFormGroup() {
        this.form = this._fb.group({
            accountSelected: this._fb.control('', [Validators.required])
        });
    }

    private _setupObservables() {
        this.subscriptions.push(
            this.form.valueChanges.subscribe(value => {
                this.isActionButtonSelected = false;
                if (value.accountSelected?.length) {
                    this.form.get('accountSelected')?.setValidators([Validators.required]);
                    this.selectAccount.emit(value.accountSelected);
                }
                if (this._onChange) {
                    this._onChange(value);
                }
            }),
            this.debouncer.pipe(debounceTime(300)).subscribe(val => this.filterAccount.emit(val))
        );
    }

    onBlurInvestorAccount() {
        this.form.updateValueAndValidity();
    }

    resetSearchInput() {
        this.accountDropdownRef()?.resetSearchInput();
    }

    private cancelAutoselectTimer() {
        if (this.autoselectTimer) {
            clearTimeout(this.autoselectTimer);
            this.autoselectTimer = null;
        }
    }

    ngOnDestroy() {
        this.cancelAutoselectTimer();
        this.isDestroying = true;
        this.form.updateValueAndValidity();
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
